/**
 * FitLite Sizer Configuration
 * Module: NovaFitLite
 * Version: 1.3.6
 *
 * v1.3.6 — the finder's missing templates (SHOP SET-UP 8, ss8-fitmap, 2026-10-16)
 *   - shop names for bodies already here: subscriptions-1717, subscriptions-6014,
 *     comfort-colors-1566-garment-dyed-crewneck-sweatshirt, baseball-bella-3200,
 *     t-shirt-gildan-64000, comfort-colors-1567-hoodi.
 *   - new MAKER reads (2026-10-16): bella-3719-hoodie, hood-comfort-color-1567,
 *     independent-hooded-2500 (ITC PRM2500), limited-edition-nublend (JERZEES 996MR).
 *     Mirror of GARMENT_HOME (drift-tested, ss8-garment-gaps.test.ts).
 *
 * v1.3.5 — P-MER's long-sleeve template name (sp52-meropen, 2026-09-30)
 *   - comfort-color-6014-long-s mapped to the 6014 (same blank, same body as
 *     comfort-colors-6014). Mirror of GARMENT_HOME (drift-tested).
 *
 * v1.3.4 — P-ALO's own template names (sp52-fitmap, 2026-09-30)
 *   - bella-3001, bella-3200-raglan, cc-1717-comfort, 1566-comfort-sweatshirt
 *     mapped, each with the blank MAKER's own chart (read 2026-09-30).
 *     Mirror of GARMENT_HOME in app/lib/fitlite/garment-home.ts (drift-tested).
 *
 * v1.3.3 — Combined garment sizes (sp51-fitcrop, 2026-09-29)
 *   - GARMENT_SIZE_GROUPS: a garment made in combined sizes (the Bella+Canvas
 *     6681 crop tee: XS/S and M/L only) maps the table's letter to its own
 *     size; a letter past its largest size is outside the chart.
 *   - sizesInOrder / sizeForLetter / nextSize / storeLabel. Every garment
 *     without groups behaves exactly as in 1.3.2.
 *
 * This file contains archetype definitions and sizing logic.
 * No external dependencies required.
 *
 * DESIGN PRINCIPLES (LOCKED):
 * - Archetypes represent fit behavior, not garment category
 * - Sleeves and necklines are variants, not behaviors
 * - Use flags for behavioral constraints, not new archetypes
 * - Keep archetype list small and stable
 *
 * v1.3.2 — Arms allocator replaces Length
 *   - The third allocator slot is now "arms" (two values: standard / tight)
 *     instead of "length" (three values). Arms tightness is a more reliable
 *     signal than length for archetypes shoppers actually compare against.
 *   - Boxy/relaxed archetypes intentionally have arms.tight = 0: the cut is
 *     already roomy in the sleeves, so a tight-arm complaint usually means
 *     the shopper needs a different style, not a different size.
 *
 * v1.3.0 — Body Type Allocators
 * Three optional secondary modifiers (torso / shoulder / arms) applied AFTER
 * the height+weight lookup and preference adjustment. Each archetype defines
 * its own per-allocator modifier table; the total is clamped to ±1 so allocators
 * never move more than one size. See applyAllocators() in the public API.
 */

window.FitLiteConfig = (function() {
  'use strict';

  // Template to archetype mapping
  // Long sleeves reuse existing archetypes (sleeve length doesn't affect torso fit)
  // V-necks reuse lightweight_fitted (with disableDownsize flag)
  const TEMPLATE_TO_ARCHETYPE = {
    // Comfort Colors - heavyweight_boxy
    'product.comfort-color-1717': 'heavyweight_boxy',
    'product.comfort-colors-6030': 'heavyweight_boxy',
    'product.comfort-colors-long-sleeve': 'heavyweight_boxy',
    'product.comfort-colors-6014': 'heavyweight_boxy',  // Long sleeve
    'product.comfort-color-6014-long-s': 'heavyweight_boxy',  // P-MER's name for the 6014
    'product.cc-1717-comfort': 'heavyweight_boxy',  // P-ALO's name for the 1717
    'product.1566-comfort-sweatshirt': 'relaxed_hoodie',  // CC 1566 fleece crewneck, "Relaxed Fit"
    'product.comfort-colors-1566-garment-dyed-crewneck-sweatshirt': 'relaxed_hoodie',  // the 1566's long name (ss8)
    'product.subscriptions-1717': 'heavyweight_boxy',  // P-CHQ subscription tee = the 1717 (ss8)
    'product.subscriptions-6014': 'heavyweight_boxy',  // P-CHQ subscription long sleeve = the 6014 (ss8)
    'product.hood-comfort-color-1567': 'relaxed_hoodie',  // CC 1567 hoodie, "Relaxed Fit" (ss8)
    'product.comfort-colors-1567-hoodi': 'relaxed_hoodie',  // P-CHQ's name for the 1567 (ss8)

// Bella+Canvas - lightweight_fitted
'product.bella-3001': 'lightweight_fitted',  // P-ALO's name for the 3001
'product.bella-3200-raglan': 'lightweight_fitted',  // 3/4 sleeve raglan, "Retail fit"
'product.baseball-bella-3200': 'lightweight_fitted',  // P-OGL's name for the 3200 (ss8)
'product.bella-3001-crew-neck': 'lightweight_fitted',
'product.bella-3501-long-sleeve': 'lightweight_fitted',
'product.bella-canvas-long-sleeve': 'lightweight_fitted',
'product.bella-3005-vneck': 'lightweight_fitted',
'product.bella-3005-v-neck': 'lightweight_fitted',  // P-CHQ's name for the 3005 (sp53-chqfit: the home maps it; this copy did not)
'product.bella-vneck': 'lightweight_fitted',
'product.bella-6400-relaxed': 'lightweight_fitted',
'product.bella-6681-crop-top': 'lightweight_fitted',  // slim crop, two sizes (XS/S, M/L)
'product.bella-tank-top-3480': 'lightweight_fitted',  // B+C 3480 unisex jersey tank, the 3001 body precedent (ss9)


    // Gildan - midweight_standard
    'product.gildan-2000': 'midweight_standard',
    'product.gildan-2400': 'midweight_standard',  // Long sleeve
    // Softstyle tee — the MyDesigns default blank (Teri 2026-06-18). Carried on
    // P-ALO's live copy by hand and in the garment home, but missing from this
    // repo copy; restored so a deploy never drops it (sp51-fitcrop).
    'product.gildan-64000': 'midweight_standard',
    'product.t-shirt-gildan-64000': 'midweight_standard',  // P-OGL's name for the 64000 (ss8)
    'product.gildan-long-sleeve': 'midweight_standard',
    'product.gildan2400-long-sleeve': 'midweight_standard',  // P-CHQ's name for the Gildan 2400 (ss9)
    'product.oversized-tees-night-shir': 'heavyweight_boxy',  // PODpartner 210U007 slit-hem tee, boxy-tee precedent (ss9)

    // Hoodies - relaxed_hoodie
    'product.gildan-18500-hoodie': 'relaxed_hoodie',
    'product.bella-3719-hoodie': 'relaxed_hoodie',  // B+C 3719, "loose fit ... Unisex sizing" (ss8)
    'product.independent-hooded-2500': 'relaxed_hoodie',  // ITC PRM2500 women's hooded pullover (ss8)
    'product.limited-edition-nublend': 'relaxed_hoodie',  // JERZEES 996MR NuBlend hoodie (ss8)
    'product.lane-seven-14001': 'relaxed_hoodie',  // Lane Seven LS14001 pullover HOODIE, "relaxed fit" (ss8)
    'product.oversized-sweatshirts': 'relaxed_hoodie',  // PODpartner 140U004 oversized crewneck, the 1566 precedent (ss9)

    // Next Level
    'product.next-level-3600': 'midweight_standard'
  };

  /**
   * GARMENT FLAGS
   * Per-template behavioral overrides that apply AFTER archetype detection.
   * Use flags for constraints that don't warrant a new archetype.
   *
   * Available flags:
   * - disableDownsize: Prevents fitted preference (and net-negative allocators)
   *                   from sizing down (V-neck rule)
   * - womensCut:       Her 2026-09-20 order. This is the women's cut of the
   *                    unisex tee — the same body, shortened — not a "relaxed
   *                    fit". Three behaviours: (1) a narrow-shoulder answer can
   *                    never cancel a full-torso size-up; (2) disableDownsize is
   *                    implied; (3) the result names the cut.
   *                    Behaviour (1) rides WOMENS_CUT_FLOOR below.
   *
   * THE SOURCE OF TRUTH for this map is app/lib/fitlite/garment-home.ts in
   * mission-control. A drift test fails the build when this file disagrees
   * with it. Edit the home, not this copy.
   */
  const GARMENT_FLAGS = {
    // V-necks: never recommend downsizing (prevents "feels smaller than expected" regret)
    'product.bella-3005-vneck': {
      disableDownsize: true
    },
    'product.bella-3005-v-neck': {
      disableDownsize: true
    },
    'product.bella-vneck': {
      disableDownsize: true
    },
    // Her women's cut. Bella+Canvas 6400: the 3001 body, about 2" shorter.
    'product.bella-6400-relaxed': {
      womensCut: true,
      disableDownsize: true
    },
    // Bella+Canvas 6681 slim crop: its M/L is narrower than a unisex S, so a
    // fitted or narrow answer never takes her a size smaller still.
    'product.bella-6681-crop-top': {
      disableDownsize: true
    }
  };

  /**
   * WOMENS_CUT_FLOOR — the dial for behaviour (1) of womensCut.
   *
   * MEASURED, 2026-09-22, over 77 live attributed orders on this garment
   * (public.fitlite_events, P-CHQ/P-ALO/P-AMZ): 25 of the 77 shoppers answered
   * full torso + narrow shoulders, and in this config those two answers cancel
   * exactly (full_torso +1, narrow -1). That is the mechanism behind her live
   * case: told L, bought XL, XL still too small.
   *
   * But 14 of those same 25 bought exactly the size they were told, and 2 bought
   * SMALLER. Replaying all 77 through the floor lowers the share told the size
   * they bought from 51/77 to 43/77. So the floor ships DEACTIVATED: the rule is
   * built and tested, and it opens when the inches question is answering
   * alongside it, which is what actually separates the 9 who needed a bigger
   * shirt from the 14 who did not.
   */
  const WOMENS_CUT_FLOOR = false;

  /**
   * GARMENT MEASUREMENTS — the blank maker's own published spec sheet, in
   * inches: half-chest laid flat, and body length from high point shoulder.
   * The product-page size chart and this table are the same numbers; there is
   * no second copy. A garment with no entry here shows NO inches rather than
   * a guessed number.
   */
  const GARMENT_MEASUREMENTS = {
    // Bella+Canvas 6400 — her women's cut
    'product.bella-6400-relaxed': {
      'S':   { chest: 17.5, length: 25.5 },
      'M':   { chest: 19,   length: 26.5 },
      'L':   { chest: 20.5, length: 27.5 },
      'XL':  { chest: 22,   length: 28.5 },
      '2XL': { chest: 24,   length: 29.5 },
      '3XL': { chest: 26,   length: 30.5 }
    },
    // Bella+Canvas 3001 body — shared by the 3001, 3501 and 3005
    'product.bella-3001-crew-neck': {
      'XS':  { chest: 16.5, length: 27 },
      'S':   { chest: 18, length: 28 },
      'M':   { chest: 20, length: 29 },
      'L':   { chest: 22, length: 30 },
      'XL':  { chest: 24, length: 31 },
      '2XL': { chest: 26, length: 32 },
      '3XL': { chest: 28, length: 33 },
      '4XL': { chest: 30, length: 34 }
    },
    // Comfort Colors 1717 body, the maker's size chart (comfortcolors.com, Sizing tab; sp52-owed2)
    'product.comfort-color-1717': {
      'S':   { chest: 18.25, length: 26.625 },
      'M':   { chest: 20.25, length: 28 },
      'L':   { chest: 22,    length: 29.375 },
      'XL':  { chest: 24,    length: 30.75 },
      '2XL': { chest: 26,    length: 31.625 },
      '3XL': { chest: 27.75, length: 32.5 },
      '4XL': { chest: 29.75, length: 33.5 }
    },
    // Bella+Canvas 6681 crop tee — the maker's own sheet,
    // bellacanvas.com/spec/6681.pdf (read 2026-09-29): body width 1" below the
    // armhole 15 5/8 / 17 1/8, body length HPS 16 1/2 / 17 1/2. Two sizes only.
    'product.bella-6681-crop-top': {
      'XS/S': { chest: 15.625, length: 16.5 },
      'M/L':  { chest: 17.125, length: 17.5 }
    },
    // sp52-fitmap — the MAKER's own charts, read 2026-09-30 (receipts in
    // app/lib/fitlite/garment-home.ts measurementSource).
    // Bella+Canvas 3001 as bellacanvas.com/spec/3001.pdf prints it (XS 16 1/2).
    'product.bella-3001': {
      'XS':  { chest: 16.5, length: 27 },
      'S':   { chest: 18,   length: 28 },
      'M':   { chest: 20,   length: 29 },
      'L':   { chest: 22,   length: 30 },
      'XL':  { chest: 24,   length: 31 },
      '2XL': { chest: 26,   length: 32 },
      '3XL': { chest: 28,   length: 33 },
      '4XL': { chest: 30,   length: 34 }
    },
    // Bella+Canvas 3200 raglan, bellacanvas.com/spec/3200.pdf (XS to 2XL only).
    'product.bella-3200-raglan': {
      'XS':  { chest: 16.125, length: 25.125 },
      'S':   { chest: 17.625, length: 27.5 },
      'M':   { chest: 19.625, length: 28.5 },
      'L':   { chest: 21.625, length: 29.5 },
      'XL':  { chest: 23.625, length: 30.5 },
      '2XL': { chest: 25.625, length: 31.5 }
    },
    // Comfort Colors 1717, the maker's size chart (comfortcolors.com, Sizing tab).
    'product.cc-1717-comfort': {
      'S':   { chest: 18.25, length: 26.625 },
      'M':   { chest: 20.25, length: 28 },
      'L':   { chest: 22,    length: 29.375 },
      'XL':  { chest: 24,    length: 30.75 },
      '2XL': { chest: 26,    length: 31.625 },
      '3XL': { chest: 27.75, length: 32.5 },
      '4XL': { chest: 29.75, length: 33.5 }
    },
    // Comfort Colors 1566 sweatshirt, the maker's size chart (Sizing tab).
    'product.1566-comfort-sweatshirt': {
      'S':   { chest: 21,   length: 27.5 },
      'M':   { chest: 23,   length: 28.5 },
      'L':   { chest: 25,   length: 29.5 },
      'XL':  { chest: 26.5, length: 30.5 },
      '2XL': { chest: 28,   length: 31.5 },
      '3XL': { chest: 29.5, length: 32.5 }
    },
    // ss8-fitmap — the MAKER's own charts, read 2026-10-16 (receipts in
    // app/lib/fitlite/garment-home.ts measurementSource).
    // Bella+Canvas 3719 hoodie, bellacanvas.com/spec/3719.pdf (XS to 2XL; the
    // sheet's 3XL/4XL columns repeat the 2XL numbers, so they are not a chart).
    'product.bella-3719-hoodie': {
      'XS':  { chest: 18,   length: 25.5 },
      'S':   { chest: 19.5, length: 26.375 },
      'M':   { chest: 21.5, length: 27.5 },
      'L':   { chest: 23.5, length: 28.625 },
      'XL':  { chest: 25.5, length: 29.75 },
      '2XL': { chest: 27.5, length: 30.875 }
    },
    // Comfort Colors 1567 hoodie, the maker's size chart (Sizing tab).
    'product.hood-comfort-color-1567': {
      'S':   { chest: 21,   length: 27.5 },
      'M':   { chest: 23,   length: 28.5 },
      'L':   { chest: 25,   length: 29.5 },
      'XL':  { chest: 26.5, length: 30.5 },
      '2XL': { chest: 28,   length: 31.5 },
      '3XL': { chest: 29.5, length: 32.5 }
    },
    // Independent Trading Co. PRM2500, the maker's size-chart image (XS to 2XL).
    'product.independent-hooded-2500': {
      'XS':  { chest: 19, length: 26 },
      'S':   { chest: 20, length: 27 },
      'M':   { chest: 21, length: 28 },
      'L':   { chest: 22, length: 29 },
      'XL':  { chest: 23, length: 30 },
      '2XL': { chest: 24, length: 31 }
    },
    // JERZEES 996MR NuBlend hoodie, the maker's sell sheet (S to 5XL).
    'product.limited-edition-nublend': {
      'S':   { chest: 20, length: 27 },
      'M':   { chest: 22, length: 28 },
      'L':   { chest: 24, length: 29 },
      'XL':  { chest: 26, length: 30 },
      '2XL': { chest: 28, length: 31 },
      '3XL': { chest: 30, length: 32 },
      '4XL': { chest: 32, length: 33 },
      '5XL': { chest: 34, length: 34 }
    },
    // Lane Seven LS14001 pullover hoodie, the maker's size specification sheet
    // (lanesevenapparel.com/static/L7_2023_Size_Specs.pdf; XS to 4XL).
    'product.lane-seven-14001': {
      'XS':  { chest: 18, length: 26 },
      'S':   { chest: 20, length: 27 },
      'M':   { chest: 22, length: 28 },
      'L':   { chest: 24, length: 29 },
      'XL':  { chest: 26, length: 30 },
      '2XL': { chest: 28, length: 31 },
      '3XL': { chest: 30, length: 32 },
      '4XL': { chest: 32, length: 33 }
    },
    // Gildan 2400 long sleeve, SanMar's copy of Gildan's G2400 spec sheet (gildan.com answers 403; S to 5XL) (ss9).
    'product.gildan-2400': {
      'S':   { chest: 18, length: 28 },
      'M':   { chest: 20, length: 29 },
      'L':   { chest: 22, length: 30 },
      'XL':  { chest: 24, length: 31 },
      '2XL': { chest: 26, length: 32 },
      '3XL': { chest: 28, length: 33 },
      '4XL': { chest: 30, length: 34 },
      '5XL': { chest: 32, length: 35 }
    },
    'product.gildan2400-long-sleeve': {
      'S':   { chest: 18, length: 28 },
      'M':   { chest: 20, length: 29 },
      'L':   { chest: 22, length: 30 },
      'XL':  { chest: 24, length: 31 },
      '2XL': { chest: 26, length: 32 },
      '3XL': { chest: 28, length: 33 },
      '4XL': { chest: 30, length: 34 },
      '5XL': { chest: 32, length: 35 }
    },
    // PODpartner 210U007 Unisex Slit Hem T-shirt, the maker's own size guide (S to XL) (ss9).
    'product.oversized-tees-night-shir': {
      'S':   { chest: 22.9, length: 28.3 },
      'M':   { chest: 23.6, length: 29.1 },
      'L':   { chest: 24.4, length: 29.9 },
      'XL':  { chest: 25.2, length: 30.7 }
    },
    // PODpartner 140U004 Heavyweight Oversized Side Slit Faded Sweatshirt, the maker's own size guide (S to XL) (ss9).
    'product.oversized-sweatshirts': {
      'S':   { chest: 25.2, length: 27.6 },
      'M':   { chest: 26.4, length: 28.3 },
      'L':   { chest: 27.2, length: 29.1 },
      'XL':  { chest: 28.0, length: 29.9 }
    },
    // Bella+Canvas 3480 unisex tank, the maker's spec sheet (XS to 3XL) (ss9).
    'product.bella-tank-top-3480': {
      'XS':  { chest: 16.25, length: 26 },
      'S':   { chest: 18.25, length: 27 },
      'M':   { chest: 20.25, length: 28 },
      'L':   { chest: 22.25, length: 29 },
      'XL':  { chest: 24.25, length: 30 },
      '2XL': { chest: 26.25, length: 31 },
      '3XL': { chest: 28.25, length: 32 }
    }
  };

  /**
   * GARMENT SIZE GROUPS — a garment made in combined sizes (sp51-fitcrop,
   * 2026-09-29). The tables below answer in single letters; this says which
   * of the garment's own sizes each letter is. A letter past the last group is
   * OUTSIDE THE CHART (the maker makes nothing bigger). `storeLabels` are the
   * spellings the live shops' Size options use (P-ALO "XS/SM", P-MER "XS/S").
   * Mirror of `sizeGroups` in app/lib/fitlite/garment-home.ts (drift-tested).
   */
  const GARMENT_SIZE_GROUPS = {
    'product.bella-6681-crop-top': [
      { label: 'XS/S', letters: ['XS', 'S'], storeLabels: ['XS/S', 'XS/SM'] },
      { label: 'M/L',  letters: ['M', 'L'],  storeLabels: ['M/L'] }
    ]
  };
  GARMENT_MEASUREMENTS['product.bella-3501-long-sleeve']   = GARMENT_MEASUREMENTS['product.bella-3001-crew-neck'];
  GARMENT_MEASUREMENTS['product.bella-canvas-long-sleeve'] = GARMENT_MEASUREMENTS['product.bella-3001-crew-neck'];
  GARMENT_MEASUREMENTS['product.bella-3005-vneck']         = GARMENT_MEASUREMENTS['product.bella-3001-crew-neck'];
  GARMENT_MEASUREMENTS['product.bella-3005-v-neck']        = GARMENT_MEASUREMENTS['product.bella-3001-crew-neck'];  // P-CHQ's V-neck (sp53-chqfit)
  GARMENT_MEASUREMENTS['product.bella-vneck']              = GARMENT_MEASUREMENTS['product.bella-3001-crew-neck'];
  GARMENT_MEASUREMENTS['product.comfort-colors-6030']      = GARMENT_MEASUREMENTS['product.comfort-color-1717'];
  GARMENT_MEASUREMENTS['product.comfort-colors-long-sleeve'] = GARMENT_MEASUREMENTS['product.comfort-color-1717'];
  GARMENT_MEASUREMENTS['product.comfort-colors-6014']      = GARMENT_MEASUREMENTS['product.comfort-color-1717'];
  GARMENT_MEASUREMENTS['product.comfort-color-6014-long-s'] = GARMENT_MEASUREMENTS['product.comfort-color-1717'];
  // ss8-fitmap: shop names for bodies already carried (one table per body, shared).
  GARMENT_MEASUREMENTS['product.subscriptions-1717']        = GARMENT_MEASUREMENTS['product.comfort-color-1717'];
  GARMENT_MEASUREMENTS['product.subscriptions-6014']        = GARMENT_MEASUREMENTS['product.comfort-color-1717'];
  GARMENT_MEASUREMENTS['product.comfort-colors-1566-garment-dyed-crewneck-sweatshirt'] = GARMENT_MEASUREMENTS['product.1566-comfort-sweatshirt'];
  GARMENT_MEASUREMENTS['product.baseball-bella-3200']       = GARMENT_MEASUREMENTS['product.bella-3200-raglan'];
  GARMENT_MEASUREMENTS['product.comfort-colors-1567-hoodi'] = GARMENT_MEASUREMENTS['product.hood-comfort-color-1567'];

  // Standard size order for comparison
  const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];

  /**
   * Archetype Definitions
   * Each archetype defines sizing behavior based on height/weight combinations
   *
   * Structure:
   * - sizeTable: 2D lookup [height range][weight range] → size
   * - heightRanges: [min, max] in total inches
   * - weightRanges: [min, max] in pounds
   * - fittedAdjust: size adjustment for "fitted" preference (-1 = size down)
   * - looserAdjust: size adjustment for "looser" preference (+1 = size up)
   * - allocatorModifiers: per-archetype torso/shoulder/arms modifier tables (v1.3+)
   * - allocatorNotes: keyed "<allocator>.<value>" → contextual result copy (v1.3+)
   */
  const ARCHETYPES = {

    /**
     * HEAVYWEIGHT_BOXY
     * Applies to: Comfort Colors 1717, Comfort Colors 6030
     * Characteristics: Generous cut, relaxed fit, heavyweight cotton
     * Sizing tendency: Runs slightly large, boxy silhouette
     */
    heavyweight_boxy: {
      fittedAdjust: -1,
      looserAdjust: 1,

      // Height ranges in total inches
      // Short: < 5'4" (64"), Average: 5'4"-5'9" (64-69"), Tall: 5'9"-6'1" (69-73"), Very Tall: > 6'1" (73"+)
      heightRanges: [
        { label: 'short', min: 0, max: 64 },
        { label: 'average', min: 64, max: 69 },
        { label: 'tall', min: 69, max: 73 },
        { label: 'very_tall', min: 73, max: 999 }
      ],

      // Weight ranges in pounds (extended to cover all body types)
      weightRanges: [
        { label: 'light', min: 0, max: 130 },
        { label: 'medium', min: 130, max: 160 },
        { label: 'athletic', min: 160, max: 190 },
        { label: 'solid', min: 190, max: 220 },
        { label: 'heavy', min: 220, max: 260 },
        { label: 'very_heavy', min: 260, max: 320 },
        { label: 'extra_heavy', min: 320, max: 400 },
        { label: 'extreme', min: 400, max: 99999 }
      ],

      // Size lookup table: sizeTable[heightIndex][weightIndex]
      sizeTable: [
        // Short (< 5'4")
        ['S', 'S', 'M', 'L', 'XL', '2XL', '3XL', '5XL'],
        // Average (5'4" - 5'9")
        ['S', 'M', 'M', 'L', 'XL', '2XL', '3XL', '5XL'],
        // Tall (5'9" - 6'1")
        ['M', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'],
        // Very Tall (> 6'1")
        ['M', 'L', 'L', 'XL', '2XL', '3XL', '4XL', '5XL']
      ],

      // Boxy already runs roomy in body and arms — narrow torso looks oversized.
      // Arms.tight is 0 here: the cut is already generous in the sleeves, so a
      // tight-arms complaint signals a different style, not a different size.
      allocatorModifiers: {
        torso:    { balanced: 0, full_torso: 0, narrow_torso: -1 },
        shoulder: { standard: 0, broad: 1,      narrow: -1 },
        arms:     { standard: 0, tight: 0 }
      },
      allocatorNotes: {
        'torso.narrow_torso':  'This style runs boxy — sizing down keeps the silhouette clean.',
        'shoulder.broad':      'Sizing up gives shoulder room without binding.'
      }
    },

    /**
     * MIDWEIGHT_STANDARD
     * Applies to: Gildan 2000, Next Level 3600
     * Characteristics: True-to-size, standard fit
     * Sizing tendency: Predictable, middle-of-road fit
     */
    midweight_standard: {
      fittedAdjust: -1,
      looserAdjust: 1,

      heightRanges: [
        { label: 'short', min: 0, max: 64 },
        { label: 'average', min: 64, max: 69 },
        { label: 'tall', min: 69, max: 73 },
        { label: 'very_tall', min: 73, max: 999 }
      ],

      weightRanges: [
        { label: 'light', min: 0, max: 130 },
        { label: 'medium', min: 130, max: 160 },
        { label: 'athletic', min: 160, max: 190 },
        { label: 'solid', min: 190, max: 220 },
        { label: 'heavy', min: 220, max: 260 },
        { label: 'very_heavy', min: 260, max: 320 },
        { label: 'extra_heavy', min: 320, max: 400 },
        { label: 'extreme', min: 400, max: 99999 }
      ],

      sizeTable: [
        // Short
        ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '5XL'],
        // Average
        ['S', 'S', 'M', 'L', 'XL', '2XL', '3XL', '5XL'],
        // Tall
        ['S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'],
        // Very Tall
        ['M', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL']
      ],

      // True-to-size: fuller torso wants +1; tight-arms also wants +1.
      allocatorModifiers: {
        torso:    { balanced: 0, full_torso: 1, narrow_torso: 0 },
        shoulder: { standard: 0, broad: 1,      narrow: -1 },
        arms:     { standard: 0, tight: 1 }
      },
      allocatorNotes: {
        'torso.full_torso':    'True-to-size styles can pull through the middle — sizing up keeps it easy.',
        'shoulder.broad':      'Sizing up keeps the shoulders from pulling.',
        'arms.tight':          'Sizing up gives more room through the arms and shoulders.'
      }
    },

    /**
     * LIGHTWEIGHT_FITTED
     * Applies to: Bella+Canvas 3001
     * Characteristics: Slim cut, lightweight, fashion-forward
     * Sizing tendency: Runs slim, shorter body length
     */
    lightweight_fitted: {
      fittedAdjust: 0,  // Already fitted, no adjustment
      looserAdjust: 1,

      heightRanges: [
        { label: 'short', min: 0, max: 64 },
        { label: 'average', min: 64, max: 69 },
        { label: 'tall', min: 69, max: 73 },
        { label: 'very_tall', min: 73, max: 999 }
      ],

weightRanges: [
  { label: 'light', min: 0, max: 120 },
  { label: 'medium', min: 120, max: 150 },
  { label: 'athletic', min: 150, max: 180 },
  { label: 'solid', min: 180, max: 210 },
  { label: 'heavy', min: 210, max: 250 },
  { label: 'very_heavy', min: 250, max: 310 },
  { label: 'extra_heavy', min: 310, max: 390 },
  { label: 'extreme', min: 390, max: 99999 }
],


      sizeTable: [
        // Short - size up slightly due to slim fit
        ['S', 'M', 'M', 'L', 'XL', '2XL', '3XL', '5XL'],
        // Average
        ['S', 'M', 'L', 'L', 'XL', '2XL', '3XL', '5XL'],
        // Tall
        ['M', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'],
        // Very Tall
        ['M', 'L', 'L', 'XL', '2XL', '3XL', '4XL', '5XL']
      ],

      // Slim cut: full_torso, broad shoulder, and tight arms all push +1.
      allocatorModifiers: {
        torso:    { balanced: 0, full_torso: 1, narrow_torso: 0 },
        shoulder: { standard: 0, broad: 1,      narrow: -1 },
        arms:     { standard: 0, tight: 1 }
      },
      allocatorNotes: {
        'torso.full_torso':    'Slim-cut styles run straight through the body — sizing up keeps it comfortable.',
        'shoulder.broad':      'Slim cut at the shoulder — sizing up prevents pulling.',
        'arms.tight':          'Bella+Canvas styles run slim in the sleeves — sizing up gives you more room through the arms.'
      }
    },

    /**
     * RELAXED_HOODIE
     * Applies to: Gildan 18500, Champion hoodies
     * Characteristics: Oversized, cozy, layering piece
     * Sizing tendency: Already relaxed, generous cut
     */
    relaxed_hoodie: {
      fittedAdjust: -1,
      looserAdjust: 1,

      heightRanges: [
        { label: 'short', min: 0, max: 64 },
        { label: 'average', min: 64, max: 69 },
        { label: 'tall', min: 69, max: 73 },
        { label: 'very_tall', min: 73, max: 999 }
      ],

      weightRanges: [
        { label: 'light', min: 0, max: 130 },
        { label: 'medium', min: 130, max: 160 },
        { label: 'athletic', min: 160, max: 190 },
        { label: 'solid', min: 190, max: 220 },
        { label: 'heavy', min: 220, max: 260 },
        { label: 'very_heavy', min: 260, max: 320 },
        { label: 'extra_heavy', min: 320, max: 400 },
        { label: 'extreme', min: 400, max: 99999 }
      ],

      sizeTable: [
        // Short - hoodies run big
        ['S', 'S', 'M', 'L', 'XL', '2XL', '3XL', '5XL'],
        // Average
        ['S', 'M', 'M', 'L', 'XL', '2XL', '3XL', '5XL'],
        // Tall
        ['M', 'M', 'L', 'L', 'XL', '2XL', '3XL', '5XL'],
        // Very Tall
        ['M', 'L', 'L', 'XL', '2XL', '3XL', '4XL', '5XL']
      ],

      // Already oversized: narrow_torso sizes down; broad shoulder still +1.
      // Arms.tight is 0: hoodies are already generous in the sleeves.
      allocatorModifiers: {
        torso:    { balanced: 0, full_torso: 0, narrow_torso: -1 },
        shoulder: { standard: 0, broad: 1,      narrow: -1 },
        arms:     { standard: 0, tight: 0 }
      },
      allocatorNotes: {
        'torso.narrow_torso':  'Hoodies run roomy — sizing down keeps the silhouette clean.',
        'shoulder.broad':      'Sizing up gives shoulder room without binding.'
      }
    },

    /**
     * UNISEX_LOOSE_CUT
     * Applies to: Generic unisex styles
     * Characteristics: Accommodating fit for all body types
     */
    unisex_loose_cut: {
      fittedAdjust: -1,
      looserAdjust: 1,

      heightRanges: [
        { label: 'short', min: 0, max: 64 },
        { label: 'average', min: 64, max: 69 },
        { label: 'tall', min: 69, max: 73 },
        { label: 'very_tall', min: 73, max: 999 }
      ],

      weightRanges: [
        { label: 'light', min: 0, max: 130 },
        { label: 'medium', min: 130, max: 160 },
        { label: 'athletic', min: 160, max: 190 },
        { label: 'solid', min: 190, max: 220 },
        { label: 'heavy', min: 220, max: 260 },
        { label: 'very_heavy', min: 260, max: 320 },
        { label: 'extra_heavy', min: 320, max: 400 },
        { label: 'extreme', min: 400, max: 99999 }
      ],

      sizeTable: [
        ['S', 'S', 'M', 'L', 'XL', '2XL', '3XL', '5XL'],
        ['S', 'M', 'M', 'L', 'XL', '2XL', '3XL', '5XL'],
        ['M', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'],
        ['M', 'L', 'L', 'XL', '2XL', '3XL', '4XL', '5XL']
      ],

      // Accommodating cut: light touch, only shoulder nudges the result.
      // Arms.tight is 0: the loose cut is already generous in the sleeves.
      allocatorModifiers: {
        torso:    { balanced: 0, full_torso: 0, narrow_torso: 0 },
        shoulder: { standard: 0, broad: 1,      narrow: -1 },
        arms:     { standard: 0, tight: 0 }
      },
      allocatorNotes: {
        'shoulder.broad':      'Sizing up gives shoulder room without binding.'
      }
    }
  };

  // Public API
  return {
    TEMPLATE_TO_ARCHETYPE: TEMPLATE_TO_ARCHETYPE,
    GARMENT_FLAGS: GARMENT_FLAGS,
    GARMENT_MEASUREMENTS: GARMENT_MEASUREMENTS,
    GARMENT_SIZE_GROUPS: GARMENT_SIZE_GROUPS,
    WOMENS_CUT_FLOOR: WOMENS_CUT_FLOOR,
    SIZE_ORDER: SIZE_ORDER,
    ARCHETYPES: ARCHETYPES,

    /**
     * Inches for one size on one template, or null when this garment has no
     * measurement on file. Null is an honest "not read in", never a zero and
     * never a guess. (row 504: a number the machine could not read is never
     * reported as a number.)
     */
    getMeasurement: function(templateName, size) {
      const t = GARMENT_MEASUREMENTS[templateName];
      if (!t) return null;
      return t[size] || null;
    },

    /**
     * CHANGE D — the shopper's own chest/bust measurement decides the size.
     * A half-chest laid flat of C inches fits a body of about 2C, so we take
     * the SMALLEST size whose garment is at least her measurement.
     *
     * Returns { size, outsideChart, largest } — and when she is past the
     * biggest size this shirt is made in, outsideChart is true and `largest`
     * carries that size with its inches, so the finder can say so plainly
     * instead of showing a confident wrong letter.
     */
    sizeFromChestInches: function(templateName, chestInches) {
      const t = GARMENT_MEASUREMENTS[templateName];
      if (!t || !chestInches || chestInches <= 0) {
        return { size: null, suggested: null, outsideChart: false, largest: null };
      }
      // Combined sizes (the 6681) answer in their own labels, maker's order.
      const ordered = this.sizesInOrder(templateName)
        .map(function(s) { return { size: s, m: t[s] }; })
        .filter(function(e) { return !!e.m; });
      if (ordered.length === 0) {
        return { size: null, suggested: null, outsideChart: false, largest: null };
      }

      const big = ordered[ordered.length - 1];
      const largest = { size: big.size, chest: big.m.chest, length: big.m.length };

      for (let i = 0; i < ordered.length; i++) {
        if (ordered[i].m.chest * 2 >= chestInches) {
          return {
            size: ordered[i].size,
            suggested: ordered[i].size,
            outsideChart: false,
            largest: largest
          };
        }
      }
      // Past the biggest size this garment is made in. `size` is the largest —
      // the anchor for the inches fact line — but `suggested` is NULL, because
      // there is no size that fits her and the panel must not print a letter
      // beside the sentence saying so (mirror of app/lib/fitlite/sizing.ts).
      return { size: big.size, suggested: null, outsideChart: true, largest: largest };
    },

    /**
     * The sizes this garment is made in, smallest first: a combined-size
     * garment's own labels, else the measured letters in SIZE_ORDER.
     * Mirror of garmentSizesInOrder() in app/lib/fitlite/sizing.ts.
     */
    sizesInOrder: function(templateName) {
      const groups = GARMENT_SIZE_GROUPS[templateName];
      if (groups && groups.length) return groups.map(function(g) { return g.label; });
      const t = GARMENT_MEASUREMENTS[templateName];
      if (!t) return [];
      return Object.keys(t)
        .filter(function(s) { return SIZE_ORDER.indexOf(s) >= 0; })
        .sort(function(a, b) { return SIZE_ORDER.indexOf(a) - SIZE_ORDER.indexOf(b); });
    },

    /**
     * The letter -> garment-size step. No groups = the letter, never outside.
     * Inside a group = that group; below the first = the first; between groups
     * = the next one UP; past the last = outsideChart (the maker makes nothing
     * bigger). Mirror of garmentSizeForLetter() in app/lib/fitlite/sizing.ts.
     */
    sizeForLetter: function(templateName, letter) {
      const groups = GARMENT_SIZE_GROUPS[templateName];
      if (!groups || !groups.length) return { size: letter, outsideChart: false };
      const i = SIZE_ORDER.indexOf(letter);
      for (let g = 0; g < groups.length; g++) {
        if (groups[g].letters.indexOf(letter) >= 0) return { size: groups[g].label, outsideChart: false };
      }
      for (let g = 0; g < groups.length; g++) {
        const top = Math.max.apply(null, groups[g].letters.map(function(l) { return SIZE_ORDER.indexOf(l); }));
        if (i <= top) return { size: groups[g].label, outsideChart: false };
      }
      return { size: groups[groups.length - 1].label, outsideChart: true };
    },

    /** The next size up this garment is made in, or null at the top. */
    nextSize: function(templateName, size) {
      const groups = GARMENT_SIZE_GROUPS[templateName];
      const order = (groups && groups.length) ? this.sizesInOrder(templateName) : SIZE_ORDER;
      const at = order.indexOf(size);
      return (at >= 0 && at + 1 < order.length) ? order[at + 1] : null;
    },

    /**
     * The spelling THIS product's Size option uses for a garment size, so the
     * panel shows the button she sees and the auto-select finds the variant
     * (P-ALO spells it "XS/SM", P-MER "XS/S"). Falls back to the size itself.
     */
    storeLabel: function(templateName, size, availableSizes) {
      const groups = GARMENT_SIZE_GROUPS[templateName];
      if (!groups || !availableSizes || !availableSizes.length) return size;
      for (let g = 0; g < groups.length; g++) {
        if (groups[g].label !== size) continue;
        for (let k = 0; k < groups[g].storeLabels.length; k++) {
          if (availableSizes.indexOf(groups[g].storeLabels[k]) >= 0) return groups[g].storeLabels[k];
        }
      }
      return size;
    },

    /**
     * Get archetype from template name
     */
    getArchetype: function(templateName) {
      return TEMPLATE_TO_ARCHETYPE[templateName] || null;
    },

    /**
     * Get archetype configuration
     */
    getArchetypeConfig: function(archetypeName) {
      return ARCHETYPES[archetypeName] || null;
    },

    /**
     * Get garment flags for a template
     * Returns flags object or empty object if no flags defined
     */
    getGarmentFlags: function(templateName) {
      return GARMENT_FLAGS[templateName] || {};
    },

    /**
     * Compare two sizes, returns: -1 if a < b, 0 if equal, 1 if a > b
     */
    compareSize: function(sizeA, sizeB) {
      const indexA = SIZE_ORDER.indexOf(sizeA);
      const indexB = SIZE_ORDER.indexOf(sizeB);
      if (indexA < indexB) return -1;
      if (indexA > indexB) return 1;
      return 0;
    },

    /**
     * Adjust size by steps (positive = size up, negative = size down)
     * Respects disableDownsize flag when provided
     */
    adjustSize: function(size, steps, flags) {
      flags = flags || {};
      const currentIndex = SIZE_ORDER.indexOf(size);
      if (currentIndex === -1) return size;

      // If disableDownsize flag is set, prevent negative adjustments.
      // A women's cut is shorter, not roomier — it never sizes a woman down.
      if ((flags.disableDownsize || flags.womensCut) && steps < 0) {
        return size;
      }

      const newIndex = Math.max(0, Math.min(SIZE_ORDER.length - 1, currentIndex + steps));
      return SIZE_ORDER[newIndex];
    },

    /**
     * Apply Body Type Allocators (v1.3+)
     *
     * Sums per-archetype torso/shoulder/arms modifiers, clamps the total to ±1
     * (allocators may never move more than one size from baseline), respects
     * disableDownsize, and returns the first matching contextual note for an
     * allocator that actually contributed a non-zero modifier.
     *
     * @param {string} baseSize         Already preference-adjusted size (e.g. 'L')
     * @param {string} archetypeName    Archetype key (e.g. 'lightweight_fitted')
     * @param {object} bodyType         { torso, shoulder, arms } — neutral values
     *                                  ('balanced', 'standard', 'standard') yield no change
     * @param {object} flags            Garment flags (disableDownsize honored)
     * @returns {{adjustedSize: string, note: (string|null)}}
     */
    applyAllocators: function(baseSize, archetypeName, bodyType, flags) {
      flags = flags || {};
      bodyType = bodyType || {};

      const archetype = ARCHETYPES[archetypeName];
      if (!archetype || !archetype.allocatorModifiers) {
        return { adjustedSize: baseSize, note: null };
      }

      const mods = archetype.allocatorModifiers;
      const torsoMod    = (mods.torso    && mods.torso[bodyType.torso])       || 0;
      const shoulderMod = (mods.shoulder && mods.shoulder[bodyType.shoulder]) || 0;
      const armsMod     = (mods.arms     && mods.arms[bodyType.arms])         || 0;

      // Clamp total: allocators must never move more than one size in either direction
      let total = Math.max(-1, Math.min(1, torsoMod + shoulderMod + armsMod));

      // WOMEN'S CUT (her 2026-09-20 order): a narrow-shoulder answer can NEVER
      // cancel a full-torso size-up. When the torso answer is full, the net is
      // floored at +1. Rides WOMENS_CUT_FLOOR — see its comment for the measurement.
      if (flags.womensCut && WOMENS_CUT_FLOOR && bodyType.torso === 'full_torso' && total < 1) {
        total = 1;
      }

      // disableDownsize: suppress any net-negative allocator move (V-neck rule).
      // A women's cut is shorter, not roomier, so it never sizes a woman down.
      if ((flags.disableDownsize || flags.womensCut) && total < 0) {
        total = 0;
      }

      const currentIndex = SIZE_ORDER.indexOf(baseSize);
      if (currentIndex === -1) {
        return { adjustedSize: baseSize, note: null };
      }

      const newIndex = Math.max(0, Math.min(SIZE_ORDER.length - 1, currentIndex + total));
      const adjustedSize = SIZE_ORDER[newIndex];

      // Note fires only when the recommendation actually moved. This avoids
      // misleading copy when modifiers cancel out, get zeroed by disableDownsize,
      // or hit the XS/5XL boundary clamps.
      let note = null;
      if (newIndex !== currentIndex) {
        const notes = archetype.allocatorNotes || {};
        const order = [
          ['torso',    bodyType.torso,    torsoMod],
          ['shoulder', bodyType.shoulder, shoulderMod],
          ['arms',     bodyType.arms,     armsMod]
        ];
        for (let i = 0; i < order.length; i++) {
          const key = order[i][0];
          const value = order[i][1];
          const mod = order[i][2];
          if (mod === 0 || !value) continue;
          const noteKey = key + '.' + value;
          if (notes[noteKey]) {
            note = notes[noteKey];
            break;
          }
        }
      }

      return { adjustedSize: adjustedSize, note: note };
    }
  };

  /*
   * v1.3.2 logic trace — verification
   *
   * Test case: 5'6" / 160 lbs / Standard preference / lightweight_fitted / arms=tight
   *   1. heightInches = (5 * 12) + 6 = 66
   *   2. lightweight_fitted.heightRanges → index 1 'average' (64–69)
   *   3. lightweight_fitted.weightRanges → 160 falls in 'athletic' (150–180), index 2
   *   4. sizeTable[1][2] = 'L'  (base size)
   *   5. preference = 'standard' → no fittedAdjust applied. baseSize stays 'L'.
   *   6. applyAllocators('L', 'lightweight_fitted',
   *                      { torso: 'balanced', shoulder: 'standard', arms: 'tight' }, {})
   *      - torsoMod    = .torso.balanced  = 0
   *      - shoulderMod = .shoulder.standard = 0
   *      - armsMod     = .arms.tight = +1
   *      - total = clamp(+1, -1, +1) = +1
   *      - flags.disableDownsize = false → no suppression
   *      - SIZE_ORDER.indexOf('L') = 3 → newIndex = 4 → 'XL'
   *      - first non-zero allocator with a note: arms.tight →
   *        "Bella+Canvas styles run slim in the sleeves — sizing up gives you more room through the arms."
   *      - returns { adjustedSize: 'XL', note: <above> }
   *   7. Final recommendation: 'XL' with the slim-sleeves note.
   *
   * Boxy "no-op for arms" check (v1.3.2 design intent):
   *   archetype = heavyweight_boxy, bodyType.arms = 'tight'
   *   → arms.tight modifier is 0 (boxy is already roomy in sleeves)
   *   → if torso/shoulder are also neutral, output is unchanged and no note fires.
   *
   * Neutral-baseline check (regression guard):
   *   bodyType = { torso: 'balanced', shoulder: 'standard', arms: 'standard' }
   *   → all three mods = 0, total = 0, adjustedSize === baseSize, note = null.
   */
})();
